import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { syncCartApi } from "@/lib/api";
import { trackAddToCart } from "@/lib/analytics";

const StoreContext = createContext(null);

const read = (key, fallback) => {
  try {
    const v = localStorage.getItem(key);
    return v ? JSON.parse(v) : fallback;
  } catch {
    return fallback;
  }
};

const FREE_SHIP_THRESHOLD = 1299;

export const StoreProvider = ({ children }) => {
  const [cart, setCart] = useState(() => read("lemce_cart", []));
  const [wishlist, setWishlist] = useState(() => read("lemce_wishlist", []));
  const [recentlyViewed, setRecentlyViewed] = useState(() => read("lemce_recent", []));
  const [cartOpen, setCartOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);

  useEffect(() => localStorage.setItem("lemce_cart", JSON.stringify(cart)), [cart]);
  useEffect(() => {
    const t = setTimeout(() => {
      syncCartApi(cart.map((i) => ({ slug: i.slug, name: i.name, qty: i.qty, price: i.price, color: i.color, size: i.size })));
    }, 1200);
    return () => clearTimeout(t);
  }, [cart]);
  useEffect(() => localStorage.setItem("lemce_wishlist", JSON.stringify(wishlist)), [wishlist]);
  useEffect(() => localStorage.setItem("lemce_recent", JSON.stringify(recentlyViewed)), [recentlyViewed]);

  const lineKey = (slug, color, size) => `${slug}__${color}__${size}`;

  const addToCart = useCallback((product, { color, size, qty = 1 } = {}) => {
    const c = color || product.colors?.[0]?.name;
    const s = size || product.sizes?.[0];
    const key = lineKey(product.slug, c, s);
    setCart((prev) => {
      const existing = prev.find((i) => i.key === key);
      if (existing) {
        return prev.map((i) => (i.key === key ? { ...i, qty: i.qty + qty } : i));
      }
      const img = product.colors?.find((x) => x.name === c)?.image || product.images?.[0];
      return [
        ...prev,
        {
          key, slug: product.slug, name: product.name, price: product.price,
          mrp: product.mrp, color: c, size: s, qty, image: img,
          category: product.category, pack_size: product.pack_size || 1,
        },
      ];
    });
    trackAddToCart(product, qty, [c, s].filter(Boolean).join(" / "));
    setCartOpen(true);
  }, []);

  const updateQty = useCallback((key, qty) => {
    setCart((prev) =>
      qty <= 0 ? prev.filter((i) => i.key !== key) : prev.map((i) => (i.key === key ? { ...i, qty } : i))
    );
  }, []);

  const removeFromCart = useCallback((key) => setCart((prev) => prev.filter((i) => i.key !== key)), []);
  const clearCart = useCallback(() => setCart([]), []);

  const toggleWishlist = useCallback((product) => {
    setWishlist((prev) => {
      if (prev.find((p) => p.slug === product.slug)) return prev.filter((p) => p.slug !== product.slug);
      return [
        ...prev,
        {
          slug: product.slug, name: product.name, price: product.price, mrp: product.mrp,
          price_display: product.price_display, mrp_display: product.mrp_display,
          image: product.images?.[0] || product.image, descriptor: product.descriptor,
          discount_pct: product.discount_pct, colors: product.colors, sizes: product.sizes,
        },
      ];
    });
  }, []);

  const isWishlisted = useCallback((slug) => wishlist.some((p) => p.slug === slug), [wishlist]);

  const addRecentlyViewed = useCallback((product) => {
    setRecentlyViewed((prev) => {
      const filtered = prev.filter((p) => p.slug !== product.slug);
      return [
        {
          slug: product.slug, name: product.name, price_display: product.price_display,
          image: product.images?.[0], descriptor: product.descriptor,
        },
        ...filtered,
      ].slice(0, 8);
    });
  }, []);

  const cartCount = cart.reduce((n, i) => n + i.qty, 0);
  const subtotal = cart.reduce((n, i) => n + i.price * i.qty, 0);
  const mrpTotal = cart.reduce((n, i) => n + i.mrp * i.qty, 0);
  const savings = mrpTotal - subtotal;
  const freeShipRemaining = Math.max(0, FREE_SHIP_THRESHOLD - subtotal);
  const freeShipPct = Math.min(100, (subtotal / FREE_SHIP_THRESHOLD) * 100);

  const value = {
    cart, cartCount, subtotal, mrpTotal, savings, freeShipRemaining, freeShipPct,
    FREE_SHIP_THRESHOLD, addToCart, updateQty, removeFromCart, clearCart,
    wishlist, toggleWishlist, isWishlisted,
    recentlyViewed, addRecentlyViewed,
    cartOpen, setCartOpen, searchOpen, setSearchOpen,
  };

  return <StoreContext.Provider value={value}>{children}</StoreContext.Provider>;
};

export const useStore = () => {
  const ctx = useContext(StoreContext);
  if (!ctx) throw new Error("useStore must be used within StoreProvider");
  return ctx;
};
